import { createEffect, onCleanup } from "solid-js";
import type { Accessor } from "solid-js";

/**
 * Freezes page scrolling while an overlay is mounted.
 *
 * Counted rather than boolean, so closing a dialog that was opened over another one does
 * not release the lock the outer overlay still needs. Removing the scrollbar also
 * narrows the viewport, so its width is added back as padding to stop the page shifting.
 */

let lockCount = 0;
let restore: (() => void) | null = null;

function acquire(): void {
  lockCount += 1;
  if (lockCount > 1 || typeof document === "undefined") return;

  const { body, documentElement } = document;
  const previousOverflow = body.style.overflow;
  const previousPaddingRight = body.style.paddingRight;
  const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

  body.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    const current = Number.parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${current + scrollbarWidth}px`;
  }

  restore = () => {
    body.style.overflow = previousOverflow;
    body.style.paddingRight = previousPaddingRight;
  };
}

function release(): void {
  lockCount = Math.max(lockCount - 1, 0);
  if (lockCount > 0) return;
  restore?.();
  restore = null;
}

export function createScrollLock(active: Accessor<boolean>): void {
  createEffect(() => {
    if (!active()) return;
    acquire();
    onCleanup(release);
  });
}
